/**
 * Sentry Error Tracking Configuration
 *
 * Integrates Sentry error monitoring with the application's error handling system.
 *
 * @module lib/logging/sentry
 */

import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry error tracking
 *
 * Only enabled in production to avoid noise in development.
 * Integrates with existing error handling infrastructure.
 */
export function initSentry() {
  // Only enable in production
  if (import.meta.env.PROD) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,

      // Integration with React
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],

      // Performance Monitoring
      // 20% sample rate — balances observability with Sentry quota usage (SEC-5)
      tracesSampleRate: 0.2,

      // Session Replay
      replaysSessionSampleRate: 0.1, // 10% of sessions
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

      // Environment
      environment: import.meta.env.MODE,

      // Release tracking
      release: import.meta.env.VITE_APP_VERSION || 'unknown',

      // Filter out noise
      beforeSend(event) {
        // Don't send errors in development
        if (import.meta.env.DEV) {
          return null;
        }

        // Filter out browser extension errors
        if (event.exception?.values?.[0]?.value?.includes('chrome-extension://')) {
          return null;
        }

        // Filter out ResizeObserver errors (browser noise)
        if (event.exception?.values?.[0]?.value?.includes('ResizeObserver loop')) {
          return null;
        }

        return event;
      },
    });

    // Set global tags for better filtering
    Sentry.setTag('app.name', 'toollife-frontend');
    Sentry.setTag('app.version', import.meta.env.VITE_APP_VERSION || 'unknown');
  }
}

/**
 * Set user context for Sentry
 *
 * Call this after successful login to track which user experienced errors.
 *
 * @param user - User information
 */
export function setSentryUser(user: { id: string; email?: string; username?: string }) {
  if (import.meta.env.PROD) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
    });
  }
}

/**
 * Clear user context (on logout)
 *
 * Call this when user logs out to stop tracking user info.
 */
export function clearSentryUser() {
  if (import.meta.env.PROD) {
    Sentry.setUser(null);
  }
}

/**
 * Add breadcrumb for user actions
 *
 * Breadcrumbs provide context about what the user was doing before an error occurred.
 *
 * @param message - Description of the action
 * @param category - Category (e.g., 'navigation', 'api', 'ui')
 * @param data - Additional context data
 */
export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, string | number | boolean>
) {
  if (import.meta.env.PROD) {
    Sentry.addBreadcrumb({
      message,
      category,
      data,
      level: 'info',
    });
  }
}

/**
 * Capture exception with context
 *
 * Sends error to Sentry with additional context information.
 *
 * @param error - Error to capture
 * @param context - Additional context (component, action, etc.)
 */
export function captureException(
  error: Error,
  context?: Record<string, string | number | boolean>
) {
  if (import.meta.env.PROD) {
    if (context) {
      Sentry.setContext('additional', context);
    }
    Sentry.captureException(error);
  }
}

/**
 * Set custom tag for error filtering
 *
 * Tags help filter and group errors in Sentry dashboard.
 *
 * @param key - Tag key
 * @param value - Tag value
 */
export function setTag(key: string, value: string) {
  if (import.meta.env.PROD) {
    Sentry.setTag(key, value);
  }
}

/**
 * Start performance transaction
 *
 * Use this to track performance of specific operations (API calls, rendering, etc.)
 *
 * @param name - Transaction name
 * @param op - Operation type (e.g., 'http.client', 'navigation')
 * @returns Transaction object (call .end() when done)
 */
export function startTransaction(name: string, op: string) {
  if (import.meta.env.PROD) {
    return Sentry.startSpan(
      {
        name,
        op,
      },
      (span) => {
        // Return wrapper with common methods
        return {
          setData: (key: string, value: string | number | boolean) => span.setAttribute(key, value),
          setStatus: (status: string) => span.setStatus({ code: status === 'ok' ? 1 : 2 }),
          end: () => span.end(),
        };
      }
    );
  }

  // Return mock transaction in development
  return {
    setData: () => {},
    setStatus: () => {},
    end: () => {},
  };
}
